import { useState, useEffect, useMemo } from "react";
import {
  BaseInput,
  Checkbox,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Show
} from "shared/components";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ArrowUp01Icon,
  Cancel01Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { cx } from "shared/utils";
import { InputSize } from "shared/components/forms/DateInput";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useDebouncedValue } from "shared/hooks/use-debounced-value";
import type { PaginatedResponse } from "shared/types/pagination";
import addressApi, { QUERY_GET_PROPERTIES } from "api/landlord/address";

const PROPERTIES_PER_PAGE = 50;

type PropertyMultiSelectDropdownProps = {
  label: string;
  selectedOptions: number[];
  onSelectionChange: (selected: number[]) => void;
};

export const PropertyMultiSelectDropdown = ({
  label,
  selectedOptions,
  onSelectionChange
}: PropertyMultiSelectDropdownProps) => {
  const [open, setOpen] = useState(false);
  const [draftSelected, setDraftSelected] = useState<number[]>(selectedOptions);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery, 500);

  useEffect(() => {
    setDraftSelected(selectedOptions);
  }, [selectedOptions]);

  const {
    data: propertiesData,
    isLoading: isLoadingProperties,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteQuery<PaginatedResponse<{ id: number; name: string }>>({
    queryKey: [
      QUERY_GET_PROPERTIES,
      debouncedSearchQuery || "",
      PROPERTIES_PER_PAGE
    ],
    queryFn: ({ pageParam }) =>
      addressApi.getProperties({
        searchTerm: debouncedSearchQuery || "",
        page: Number(pageParam),
        per_page: PROPERTIES_PER_PAGE
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? lastPage.current_page + 1 : undefined,
    enabled: open
  });

  const propertiesOptions = useMemo(() => {
    const loadedProperties =
      propertiesData?.pages.flatMap((page) =>
        (page.items ?? []).map((property) => ({
          id: property.id,
          name: property.name || `Property ${property.id}`
        }))
      ) ?? [];

    const selectedPropertyIds = new Set(loadedProperties.map((p) => p.id));
    const missingSelectedProperties = draftSelected
      .filter((id) => !selectedPropertyIds.has(id))
      .map((id) => ({
        id,
        name: `Property ${id}`
      }));

    return [...loadedProperties, ...missingSelectedProperties];
  }, [propertiesData, draftSelected]);

  const handleToggle = (id: number, e?: React.ChangeEvent) => {
    if (e) e.stopPropagation();
    const newSelection = draftSelected.includes(id)
      ? draftSelected.filter((item) => item !== id)
      : [...draftSelected, id];
    setDraftSelected(newSelection);
    onSelectionChange(newSelection);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDraftSelected([]);
    onSelectionChange([]);
    setSearchQuery("");
  };

  const hasSelection = draftSelected.length > 0;

  useEffect(() => {
    if (!open) {
      setDraftSelected(selectedOptions);
      setSearchQuery("");
    }
  }, [open, selectedOptions]);

  let listContent: React.ReactNode;
  if (isLoadingProperties)
    listContent = <p className="text-sm text-content-400">Loading...</p>;
  else if (propertiesOptions.length > 0)
    listContent = propertiesOptions.map((option) => (
      <Checkbox
        key={option.id}
        id={`${label.toLowerCase().replace(/\s+/g, "-")}-${option.id}`}
        label={option.name}
        checked={draftSelected.includes(option.id)}
        onChange={(e) => handleToggle(option.id, e)}
      />
    ));
  else
    listContent = <p className="text-sm text-content-400">No results found</p>;

  return (
    <div className="border-b border-alpha-40 pb-4">
      <button
        className="w-full"
        type="button"
        onClick={() => setOpen((prev) => !prev)}
      >
        <div className="flex w-full justify-between items-center">
          <div className="flex items-center gap-2">
            <p className="text-sm font-bold text-content-1000">{label}</p>
            {hasSelection && (
              <span className="text-xs text-content-400">
                ({draftSelected.length})
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 h-[40px]">
            {hasSelection ? (
              <IconButton
                HugeIcon={Cancel01Icon}
                variant={IconButtonVariant.BASE}
                size={IconButtonSize.SM}
                borderLess
                className="!p-0.5 stroke-content-500 shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClear(e);
                }}
              />
            ) : null}
            <HugeiconsIcon
              icon={ArrowUp01Icon}
              className={cx("transition-all", open ? "rotate-0" : "rotate-180")}
            />
          </div>
        </div>
      </button>
      <Show if={open}>
        <div
          className="mt-4 flex flex-col gap-3"
          onClick={(e) => e.stopPropagation()}
        >
          <BaseInput
            id={`${label.toLowerCase().replace(/\s+/g, "-")}-search`}
            label=""
            placeholder="Search"
            value={searchQuery}
            onChange={(value) => setSearchQuery(value)}
            className="!mb-0"
            size={InputSize.SM}
          />
          <div className="flex flex-col gap-3 max-h-[200px] overflow-y-auto">
            {listContent}
          </div>
          <Show if={!isLoadingProperties && hasNextPage}>
            <button
              type="button"
              className="text-primary-500 text-sm font-bold self-start disabled:opacity-50"
              disabled={isFetchingNextPage}
              onClick={() => fetchNextPage()}
            >
              {isFetchingNextPage ? "Loading..." : "Show more"}
            </button>
          </Show>
        </div>
      </Show>
    </div>
  );
};
